// Parks a tarot draw across the login step, and gives that draw an identity.
//
// Google sign-in is a full page navigation and phone OTP remounts the tree, so
// in-memory state does not survive "Get free reading" -> sign in -> back. The
// draw therefore goes to sessionStorage, exactly like the guest chat question in
// useGuestChat.ts, and is replayed once the user lands back signed in.
//
// sessionStorage, not localStorage: a draw must not outlive the tab. Coming back
// tomorrow should feel like a fresh shuffle, not a stale one.

import type { DrawnCard } from "../data/tarotDeck";

export const TAROT_PENDING_KEY = "auraTarotPendingDraw";

// If they wandered off for an hour, the moment has passed — drop it and let them
// draw again rather than resurrecting cards they no longer remember picking.
const PENDING_MAX_AGE_MS = 60 * 60 * 1000;

export interface PendingDraw {
  cards: DrawnCard[];
  question: string;
  drawId: string;
}

/**
 * Mint the idempotency key for one spread.
 *
 * The server charges at most once per (user, draw_id) — the claim INSERT is
 * unique on that pair, so a second request for the same draw loses the race and
 * is handed the winner's reading without ever reaching the debit. That makes
 * this value the ONLY thing standing between a double-tap and a double charge,
 * so it must be minted once per spread and then held still: across retries, a
 * page reload, and the login round trip.
 *
 * randomUUID needs a secure context and is missing on Safari before 15.4, and
 * silently having no draw id is exactly the failure this prevents — so there is
 * a getRandomValues fallback rather than an optional call.
 */
export function newDrawId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the manual build */
  }
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10
  const hex: string[] = [];
  for (let i = 0; i < 16; i++) hex.push(b[i].toString(16).padStart(2, "0"));
  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}

export function savePendingDraw(cards: DrawnCard[], question: string, drawId: string) {
  try {
    sessionStorage.setItem(
      TAROT_PENDING_KEY,
      JSON.stringify({ cards, question, drawId, ts: Date.now() }),
    );
  } catch {
    /* storage blocked — they simply re-pick after signing in */
  }
}

/**
 * Read AND clear the parked draw.
 *
 * Clearing before returning keeps it single-use: a remount, a StrictMode double
 * effect or a fast refresh can never replay the same draw and spend the user's
 * one free reading twice.
 *
 * The parked drawId comes back with it, which is the point — the replay after
 * login is a RETRY of the same draw, not a new one, and giving it a fresh id
 * would reopen the double-charge window at exactly the moment the flow is least
 * predictable.
 */
export function takePendingDraw(): PendingDraw | null {
  try {
    const raw = sessionStorage.getItem(TAROT_PENDING_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(TAROT_PENDING_KEY);
    const { cards, question, drawId, ts } = JSON.parse(raw);
    if (!Array.isArray(cards) || cards.length !== 3) return null;
    if (!cards.every((c) => c && typeof c.id === "string")) return null;
    if (!ts || Date.now() - ts > PENDING_MAX_AGE_MS) return null;
    return {
      cards,
      question: typeof question === "string" ? question : "",
      // A draw parked by an older build has no id. Minting one here is correct:
      // it is still a single draw, and one id is what the guard needs.
      drawId: typeof drawId === "string" && drawId ? drawId : newDrawId(),
    };
  } catch {
    return null;
  }
}

export function clearPendingDraw() {
  try {
    sessionStorage.removeItem(TAROT_PENDING_KEY);
  } catch {
    /* nothing to do */
  }
}
